import { useQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import {
  GetTaskComments as GetTaskCommentsGenerated,
  GetTaskCommentsVariables as GetTaskCommentsVariablesGenerated,
  GetTaskComments_task_taskFeedItemConnections_taskFeedItems_Task as TaskActionGenerated,
  GetTaskComments_task_taskFeedItemConnections_taskFeedItems_TaskActivity as TaskActivityGenerated,
  GetTaskComments_task_taskFeedItemConnections_taskFeedItems_TaskActivity_metadata_ScheduleChangeMetadata as TaskActivityScheduleChangeMetadataGenerated,
  GetTaskComments_task_taskFeedItemConnections_taskFeedItems_Comment_attachments as TaskAttachmentGenerated,
  GetTaskComments_task_taskFeedItemConnections_taskFeedItems_Comment as TaskCommentGenerated,
  GetTaskComments_task as TaskDataGenerated,
} from "core/queries/__generated__/GetTaskComments";

// Queries
export interface TaskData extends TaskDataGenerated {}
export interface GetTaskCommentsResponse extends GetTaskCommentsGenerated {}
export interface GetTaskCommentsVariables
  extends GetTaskCommentsVariablesGenerated {}
export interface TaskActivity extends TaskActivityGenerated {}
export interface TaskComment extends TaskCommentGenerated {}
export interface TaskAttachment extends TaskAttachmentGenerated {}
export interface TaskAction extends TaskActionGenerated {}
export interface TaskActivityScheduleChangeMetadata
  extends TaskActivityScheduleChangeMetadataGenerated {}

export enum ActivityTypenames {
  LastViewedAt = "LastViewedAt",
  TaskAction = "Task",
  TaskComment = "Comment",
  TaskActivity = "TaskActivity",
}

export const getTask = gql`
  query GetTaskComments($taskId: ID!) {
    task(id: $taskId) {
      id
      taskFeedItemConnections {
        lastViewedAt
        unreadCount
        taskFeedItems {
          id
          __typename
          author {
            id
            name
            avatar {
              id
              url
            }
          }
          body
          createdAt
          ... on TaskActivity {
            activityTime
            activityType
            prevValue
            nextValue
            isSubtaskActivity
            metadata {
              # Please keep this in sync with GetNotifications metadata.
              # Bad things can happen if you don't. https://app.asana.com/0/1135692148853775/1201719083791219
              ... on AssigneeIdChangeMetadata {
                id
                newAssignee {
                  id
                  name
                }
                prevAssignee {
                  id
                  name
                }
              }
              ... on ChangedTagMetadata {
                id
                tag {
                  id
                  name
                }
              }
              ... on DueAtChangeMetadata {
                id
                newDueAt
                prevDueAt
              }
              ... on EtaChangeMetadata {
                id
                newEta
                reason
              }
              ... on NameChangeMetadata {
                id
                newName
                previousName
              }
              ... on PriorityChangeMetadata {
                id
                newPriority
                previousPriority
              }
              ... on RecurrenceChangeMetadata {
                id
                daysOfWeek
              }
              ... on ScheduleChangeMetadata {
                id
                reason
                prevAssignees {
                  id
                  name
                }
              }
              ... on StatusChangeMetadata {
                id
                newStatus
                prevStatus
              }
            }
          }
          ... on Task {
            completedAt
            notes
            status
          }
          ... on Comment {
            attachments {
              resourceType
              id
              url
            }
            deletedAt
            richBody
            mentions {
              items {
                ... on User {
                  id
                }
              }
            }
          }
        }
      }
    }
  }
`;

interface UseGetTaskCommentsQueryOptions {
  variables: GetTaskCommentsVariables;
  pollInterval?: number;
  skip?: boolean;
}
export const useGetTaskCommentsQuery = ({
  variables,
  pollInterval,
  skip = false,
}: UseGetTaskCommentsQueryOptions) =>
  useQuery<GetTaskCommentsResponse, GetTaskCommentsVariables>(getTask, {
    fetchPolicy: "cache-and-network",
    skip,
    variables,
    pollInterval,
  });
